/**
 * dashboard/derive.ts — pure, side-effect-free derivations for the social-hero
 * dashboard (the /app no-recipe landing).
 *
 * Every value the hero renders comes from data the /app route ALREADY loads —
 * the `recipes` array (Recipe[] with per-row `stats`). NO new backend call, no
 * new fetch: these helpers only fold over what is already in memory. Kept pure
 * and separate from the component so the derivations are unit-testable without a
 * DOM.
 *
 * Refs: design.md §2 (quiet register, honest state — never fabricate a number),
 * branding.md §5 (voice) / §8 (vocabulary: Draft, Cook, Recipe).
 */

import type { Recipe } from "~/features/explorer/types";

// ─── Status line ────────────────────────────────────────────────────────────

/** The three honest counts the mono status line reports. */
export interface DashboardStatus {
  /** Total recipes in the library. */
  recipes: number;
  /** Recipes carrying a live Draft (current-work signal — rendered in accent). */
  drafts: number;
  /** Terminal-attempt (cook) count summed across the library, when known. */
  cooks: number;
}

/**
 * Fold the loaded `recipes` into the status-line counts. `stats` may be absent
 * on a row (UNKNOWN — stale SWR snapshot mid-revalidation, per types.ts); such
 * rows contribute 0 cooks rather than a fabricated number.
 */
export function deriveDashboardStatus(recipes: Recipe[]): DashboardStatus {
  let drafts = 0;
  let cooks = 0;
  for (const r of recipes) {
    if (r.hasDraft === true) drafts += 1;
    if (r.stats !== undefined) cooks += r.stats.cooks;
  }
  return { recipes: recipes.length, drafts, cooks };
}

// ─── Resume candidate ───────────────────────────────────────────────────────

/**
 * The recency timestamp for a recipe: the later of its last edit and its last
 * cook. Both are already loaded on the row (no fetch). A recipe never cooked
 * falls back to its edit time.
 */
export function recipeRecency(recipe: Recipe): number {
  const cookedAt = recipe.stats?.lastCookedAtMs ?? 0;
  // Defensive: a missing / NaN edit timestamp must not poison the max (NaN would
  // sink a recently-cooked recipe out of Resume). Coerce a falsy/NaN edit to 0 so
  // the cook time still surfaces it.
  const editedAt = recipe.lastEditedAt || 0;
  return Math.max(editedAt, cookedAt);
}

/**
 * The single most-recently-touched recipe — the Resume target. Returns null for
 * an empty library (a brand-new user simply has no Resume row). Ties resolve to
 * the earlier array position (recipe.list already returns updated_at DESC, so
 * the first row wins a tie, which is the intended "most recent").
 */
export function deriveResumeRecipe(recipes: Recipe[]): Recipe | null {
  let best: Recipe | null = null;
  let bestRecency = -Infinity;
  for (const r of recipes) {
    const recency = recipeRecency(r);
    if (recency > bestRecency) {
      best = r;
      bestRecency = recency;
    }
  }
  return best;
}

// ─── Greeting ───────────────────────────────────────────────────────────────

/**
 * The time-of-day greeting stem (no name, no punctuation). Quiet + unsentimental
 * per branding.md §5 — no exclamations, ever. Boundaries: morning [5,12),
 * afternoon [12,18), evening otherwise.
 */
export function greetingForHour(hour: number): string {
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 18) return "Good afternoon";
  return "Good evening";
}
